import React, {
    ReactElement,
} from "react";
import {
    ChannelMode,
    ChatSettingsMicrosoftTeams, ChatSettingsMicrosoftTeamsInput,
} from "../../../../../__generated__/api";
import { ReactComponent as TeamsLogo } from "../../../../../assets/img/microsoft-teams.svg";
import { ProxyProperty, useFormBuilder } from "../../../../../components/dirty-tracker/FormTracker";
import ChannelTags from "../../../../../components/pickers/channel-tags/ChannelTags";
import Select from "../../../../../components/select/Select";
import SettingsContainer from "../../../../../components/theme/settings";
import { Link, Paths } from "../../../../../contexts/Routing";
import { useSetTitle } from "../../base";
import ChannelModeHelp from "../../channels/slack/ChannelModeHelp";

import Styles from "../Chat.module.scss";

export interface Props {
    data: ProxyProperty<ChatSettingsMicrosoftTeams>;
    canAddTags: boolean;
}

const CHANNEL_MODES = {
    [ChannelMode.Threaded]: "Threaded",
    [ChannelMode.Manual]: "Manual",
};

export default function MicrosoftTeamsSettings({
    data, canAddTags,
}: Props): ReactElement {
    const properties = data.useBuilder(
        builder => builder
            .property("tags", current => current?.tags ?? [])
            .property("channelMode", current => current?.channelMode),
    );

    useSetTitle(data?.value?.name ? (
        <>
            <TeamsLogo className={Styles["nav-icon"]} />
            {" "}
            {data?.value?.name}
        </>
    ) : undefined, [data]);

    return (

        <SettingsContainer.Section
            title="Microsoft Teams"
            helpText="Settings specific to how Foqal interacts with Microsoft Teams."
        >
            {canAddTags && (
                <SettingsContainer.Setting
                    title="Channel Groups"
                    description="Which Channel Groups should be associated with this queue.
                    To manage channel groups, visit Channel Groups"
                    descriptionElement={(
                        <>
                            Which tags should be associated with this queue. To manage channel tags, visit
                            {" "}
                            <Link
                                to={Paths.ADMINISTRATOR_SETTING}
                                params={{ setting: "channel-tags" }}
                            >
                                General Settings
                            </Link>
                        </>
                    )}
                    permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
                >
                    <ChannelTags
                        isMulti
                        value={properties.tags.value}
                        onSelected={properties.tags.setValue}
                        canCreate
                    />
                </SettingsContainer.Setting>

            )}

            <SettingsContainer.Setting
                title="Channel Mode"
                description="When a user types a messages in a channel, should that create a new
                                       ticket and thread, or should ticket creation be done manually using features such
                                       as emoji actions or the channel menu?"
                descriptionElement={(
                    <>
                        When a user types a messages in a channel, should that create a new
                        ticket and thread, or should ticket creation be done manually using features such
                        as emoji actions or the channel menu?
                        {" "}
                        <ChannelModeHelp iconVariant="default" iconSize="sm" />
                    </>

                )}
                permissions={SettingsContainer.Setting.Permissions.GroupAdministrator}
            >
                <Select
                    className={SettingsContainer.Setting.PickerClassName}
                    placeholder=""
                    isClearable
                    onChange={value => properties.channelMode.setValue(value?.value as ChannelMode)}
                    options={Object.entries(CHANNEL_MODES).map(([value, label]) => ({ value, label }))}
                    value={!properties.channelMode.value ? null : {
                        value: properties.channelMode.value,
                        label: CHANNEL_MODES[properties.channelMode.value as keyof typeof CHANNEL_MODES],
                    }}
                />
            </SettingsContainer.Setting>

        </SettingsContainer.Section>

    );
}

MicrosoftTeamsSettings.defaultProps = {
};
